import React, { useCallback, useEffect } from 'react';
import { FileError, useDropzone } from 'react-dropzone';
import { Preloader } from '@common-fe/common-fe';

import { ACCEPTED_FILE_TYPES, FileImage, MAX_SIZE_OF_FILE } from '@/common/constants';
import prepareFile from '@/components/controls/DropScan/prepareFile';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import useOnDrop from '@/components/controls/DropScan/useOnDrop';

import { FileGetAndUploadButton, FileGetAndUploadButtonProps } from './FileGetAndUploadButton';

interface Props extends Partial<FileGetAndUploadButtonProps> {
  id: string;
  onUpload?: () => void;
  onFileUploadError?: (error?: FileError) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  storeKey?: string;
  maxFiles?: number;
  multiple?: boolean;
  isFileLoading?: boolean;
  disableFilesUpload?: boolean;
  ocrRequired?: boolean;
}

const FileGetAndUploadDropButton: React.FC<Props> = ({
  id,
  onUpload,
  onFileUploadError,
  onLoadingChange,
  storeKey,
  maxFiles,
  disableFilesUpload = true,
  ocrRequired = false,
  onChange,
  accept,
  multiple,
  isFileLoading,
  ...props
}) => {
  const {
    values: { files: existingFiles },
    handleSetValues,
  } = useFilesInDocumentStore(storeKey);

  const onFilesAdded = useCallback(
    (acceptedFiles: FileImage[]) => {
      if (acceptedFiles?.length) {
        const preparedFiles = acceptedFiles.map(prepareFile);
        handleSetValues({ files: [...existingFiles, ...preparedFiles] });
        onUpload?.();
      }
    },
    [existingFiles, handleSetValues, onUpload]
  );

  const { onDrop, isLoading } = useOnDrop({
    onFilesAdded,
    onFileUploadError,
    disableFilesUpload,
    storeKey,
    ocrRequired,
  });

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const { getInputProps } = useDropzone({
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_SIZE_OF_FILE,
    onDrop,
    noDrag: true,
    noClick: true,
    multiple: multiple || false,
    maxFiles: maxFiles || 1,
  });

  const dropzoneInputProps = getInputProps();

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(event);
      }

      if (dropzoneInputProps.onChange) {
        dropzoneInputProps.onChange(event);
      }
    },
    [onChange, dropzoneInputProps]
  );

  return (
    <FileGetAndUploadButton
      onChange={handleFileChange}
      accept={accept}
      id={id}
      {...props}
      disabled={isLoading || isFileLoading}
      title={isLoading || isFileLoading ? undefined : props.title}
      icon={
        isLoading || isFileLoading ? (
          <Preloader testId="upload-button_preloader" color="white" />
        ) : (
          props.icon
        )
      }
      isRightIconPosition={isLoading || isFileLoading ? false : props.isRightIconPosition}
    />
  );
};

export default FileGetAndUploadDropButton;
